import { useRouter } from "vue-router";
import type { Offer } from "@/types/Offers";
import type { ErrorResponse } from "@/types/global";
import offersHelper from "@/utils/helpers/offers";
import pathTextHelper from "@/utils/helpers/pathText";
import notificationWrapper from "@/utils/helpers/notificationWrapper";
import { createMoveManagerContext, type MoveManagerContext, type MoveManagerProps, type useMoveManagerOverrides } from "./moveManagerBase";
import { useMoveManagerOfferPart } from "./useMoveManagerOffer";
import { useMoveManagerPricePart } from "./useMoveManagerPrice";

export function useMoveManagerMethodsPart(
	props?: MoveManagerProps,
	{ emit }: { emit: (event: string, ...args: unknown[]) => void } = { emit: () => {} },
	overrides?: useMoveManagerOverrides,
	ctx?: MoveManagerContext,
) {
	const context = ctx ?? createMoveManagerContext(props);
	const { isIndividual } = useMoveManagerOfferPart(props, context);
	const { arrhesPercent, arrhesAlreadyPaid, floaAlreadyPaid } = useMoveManagerPricePart(props, context);
	const { offer, currentOffer, prebookDate, offers, offersLoading, offerStore, configStore } = context;
	const router = useRouter();

	const setOffer = () => {
		const offerTmp = offersHelper.getValidOffer(offers.value, !!isIndividual.value);
		if (offerTmp && offerTmp.failed_payment) {
			offer.value = offerTmp;
			offersLoading.value = false;
		}
	};

	const getTextVersion = (text: string, paramsToMerge?: any) => {
		return pathTextHelper.getTextVersion(text, {
			...paramsToMerge,
			isIndividual: isIndividual.value,
			arrhesPercent: arrhesPercent.value,
			arrhesAlreadyPaid: arrhesAlreadyPaid.value,
			floaAlreadyPaid: !!floaAlreadyPaid.value?.dealReference,
		});
	};

	const preBook = async (token: string, _intent: string, _authenticatePayment: (intent: string) => void, paymentType: string) => {
		if (!currentOffer.value) return false;
		configStore.setLoading(true);
		const offerTmp: Offer & { payment_type?: string } = {
			...currentOffer.value,
			prebook_date: prebookDate.value,
			floa_reference_id: floaAlreadyPaid.value?.dealReference,
			stripe_intent_id: token,
			payment_type: paymentType || undefined,
		};
		try {
			await offerStore.preBookOffer(offerTmp);
			emit('update:offer', offerTmp);
			return true;
		} catch (error) {
			configStore.setLoading(true);
			return false;
		} finally {
			configStore.setLoading(false);
		}
	};

	const book = async (token: string, paymentIntent: string, authenticateMethod: (intent: string) => void, paymentType?: string) => {
		configStore.setLoading(true);
		const offerTmp: any = {
			...currentOffer.value,
		};
		if (token) {
			offerTmp.stripe_intent_id = token;
		}
		if (paymentIntent) {
			offerTmp.payment_intent = paymentIntent;
		}
		if (paymentType) {
			offerTmp.payment_type = paymentType;
		}
		try {
			const data = await offerStore.bookOffer(offerTmp);
			emit('update:offer', offerTmp);
			configStore.setLoading(false);
			return data;
		} catch (error: ErrorResponse) {
			// PAYMENT REFUSED - INDIVIDUAL CASE
			if (error && error.status === 402 && error.data) {
				const {
					error_code: errorCode,
					payment_intent: intent,
					decline_code: declineCode,
				} = error.data;
				if (authenticateMethod && intent && errorCode === 'authentication_required') {
					authenticateMethod(intent);
					return false;
				}

				const stripeCode = (errorCode === 'card_declined' && declineCode) || errorCode;
				notificationWrapper('stripe', stripeCode);
				if (overrides?.onStripeError) overrides.onStripeError(stripeCode);
			}
			if (error?.status === 404) {
				router.push({
					name: 'comparator',
				}).catch((e) => { });
			}
			configStore.setLoading(false);
			return false;
		}
	}

	const choose = async () => {
		if (!currentOffer.value) return false;
		configStore.setLoading(true);
		const offerTmp: Offer = {
			...currentOffer.value,
		};
		try {
			await offerStore.chooseOffer(offerTmp);
			emit('update:offer', offerTmp);
			return true;
		} catch (error) {
			return false;
		} finally {
			configStore.setLoading(false);
		}
	};

	return {
		setOffer,
		getTextVersion,
		preBook,
		book,
		choose,
	};
}

